import { useEffect, useState } from 'react';
import { Identify, identify } from '@amplitude/analytics-browser';
import { initAmplitude, initPendo } from './utils';
import { RouterProvider } from 'react-router-dom';
import 'mapbox-gl/dist/mapbox-gl.css';
import Router from 'pages/Router';
import { CookieBot } from 'components/CookieBot';
import './App.less';
import { isPendoEnvironmentSupported } from 'utils/Pendo';
import { HeadSectionGA } from 'components/HeadSection';
import { useElementInView } from 'hooks/useElementInView';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from 'hooks/useCookieConsent';

const setupHeightViewPortForMobile = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

function App() {
  const hasScrolledToBottom = useElementInView('footerSection');
  const [hasSpent60Seconds, setHasSpent60Seconds] = useState(false);
  const [showAmplitudeSurvey, setShowAmplitudeSurvey] = useState(false);
  const { i18n } = useTranslation();
  const { statistics } = useCookieConsent();

  useEffect(() => {
    const timer = setTimeout(() => {
      setHasSpent60Seconds(true);
    }, 60000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setupHeightViewPortForMobile();
  }, []);

  useEffect(() => {
    const isSeedsOnboarding =
      new URLSearchParams(window.location.search).get('redirect') === 'seedsOnboarding';
    if (hasScrolledToBottom && hasSpent60Seconds && !isSeedsOnboarding) {
      setShowAmplitudeSurvey(true);
    }
  }, [hasScrolledToBottom, hasSpent60Seconds]);

  /*
   * Amplitude and Pendo are "statistics" cookies: they start only once Cookiebot
   * reports statistics consent. Teardown on withdrawal is owned by
   * utils/analyticsConsent, wired up once in index.tsx.
   * `statistics` (not `method`) is the gate — an explicit *decline* is still an
   * explicit consent method.
   */
  useEffect(() => {
    if (!statistics) {
      return;
    }

    const currentLocale = i18n.language || 'en';
    initAmplitude(showAmplitudeSurvey, currentLocale);

    if (process.env.REACT_APP_ENV && isPendoEnvironmentSupported(process.env.REACT_APP_ENV)) {
      initPendo('dev');
    }

    const identifyObj = new Identify();
    identifyObj.set('location', 'LAX');
    identify(identifyObj);
  }, [showAmplitudeSurvey, i18n.language, statistics]);

  return (
    <div className="App" data-testid="app-main-component">
      <HeadSectionGA />
      <RouterProvider router={Router} />
      <CookieBot />
    </div>
  );
}

export default App;
